import {
  Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Typography, Select, MenuItem, TextField, Button, Box, Chip, Alert
} from "@mui/material";
import { Check, Warning, Block, Delete, Report } from "@mui/icons-material";
import React from "react";

const getReportTypeColor = (reportType) => {
  const dangerTypes = ["HATEFULL", "SEXUAL", "VIOLENCE", "THREATS", "SEXUAL_HARASSMENT", "RACIST"];
  const warningTypes = ["SELF-HARM", "VULGAR", "SPAM"];
  const infoTypes = ["FAKE", "COPYRIGHT", "IMPERSONATE", "OTHER"];
  
  if (dangerTypes.includes(reportType)) return "error";
  if (warningTypes.includes(reportType)) return "warning";
  if (infoTypes.includes(reportType)) return "info";
  return "default";
};

const getAIValidationColor = (validation) => {
  if (validation === "SAFE") return "success";
  if (["HATEFULL", "VIOLENCE", "SEXUAL"].includes(validation)) return "error";
  if (validation === "SELF-HARM") return "warning";
  return "default";
};

export default function ReportActionDialog({
  open, onClose, onSubmit, selectedReport, actionType, setActionType,
  responseMessage, setResponseMessage
}) {
  console.log("Selected Report in Dialog:", selectedReport);
  const isMessageReport = selectedReport?.type === "MESSAGE";
  
  // Determine which report type to display
  const getReportTypeField = () => {
    if (selectedReport?.type === "PROFILE" && selectedReport.profileReportType) {
      return selectedReport.profileReportType;
    }
    if (selectedReport?.type === "MESSAGE" && selectedReport.messageReportType) {
      return selectedReport.messageReportType;
    }
    if (selectedReport?.contentReportType) {
      return selectedReport.contentReportType;
    }
    return null;
  };

  const reportTypeField = getReportTypeField();
  
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Take Action on Report</DialogTitle>
      <DialogContent>
        {selectedReport && (
          <>
            <DialogContentText gutterBottom p={0} m={0}>
              You are taking action on <strong>{selectedReport.reportedUser?.fullname}</strong>.
            </DialogContentText>
            
            <Box sx={{ my: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip 
                label={`Type: ${selectedReport.type}`} 
                size="small" 
                color="primary"
                variant="outlined"
              />
              {reportTypeField && (
                <Chip 
                  label={reportTypeField.replace(/_/g, ' ')} 
                  size="small" 
                  color={getReportTypeColor(reportTypeField)}
                />
              )}
              {selectedReport.AIValidation && (
                <Chip 
                  label={`AI: ${selectedReport.AIValidation}`} 
                  size="small" 
                  color={getAIValidationColor(selectedReport.AIValidation)}
                  icon={selectedReport.AIValidation === "SAFE" ? <Check fontSize="small" /> : <Warning fontSize="small" />}
                />
              )}
              {isMessageReport && (
                <Chip 
                  label="Message will be deleted" 
                  size="small" 
                  color="warning"
                  icon={<Delete />}
                />
              )}
            </Box>

            <DialogContentText gutterBottom p={0} m={0}>
              <strong>Details:</strong> {selectedReport.details}
            </DialogContentText>
            
            {selectedReport.messageText && (
              <Alert severity="info" sx={{ my: 2 }}>
                <Typography variant="caption" display="block">
                  <strong>Reported Message:</strong>
                </Typography>
                <Typography variant="body2" p={0} m={0}>
                  {selectedReport.messageText}
                </Typography>
              </Alert>
            )}

            <Box sx={{ my: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Select Action</Typography>
              <Select fullWidth value={actionType} onChange={(e) => setActionType(e.target.value)}>
                <MenuItem value="WARN">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Warning fontSize="small" />
                    Issue Warning (via System Message)
                  </Box>
                </MenuItem>
                <MenuItem value="DISABLE">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Block fontSize="small" />
                    Disable User Account
                  </Box>
                </MenuItem>
                <MenuItem value="BAN">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Report fontSize="small" />
                    Ban User (Permanent Disable)
                  </Box>
                </MenuItem>
              </Select>
              
              <TextField
                fullWidth 
                multiline 
                rows={4} 
                label="Response Message (Optional)"
                placeholder="Leave blank to use default message for this action..."
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                sx={{ mt: 2 }}
                helperText={
                  actionType === "WARN" 
                    ? "Custom message will be sent to the user as a system warning. If blank, a default warning message will be used." 
                    : "Custom message will be logged with the report action. If blank, a default message will be used."
                }
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onSubmit} startIcon={<Check />}>
          Confirm Action
        </Button>
      </DialogActions>
    </Dialog>
  );
}
